import React, { useEffect, useState, useRef, useMemo } from "react";
import { Button, Grid, Menu, Select, TextInput } from "@mantine/core";
import { useAtom } from "jotai";
import _ from "lodash";
import ManageProgressBar from "../manageProgressBar";
import MGAAPI from "../../api/index";
import { projectDetailsAtom } from "../../utils/atoms/projectDetailsAtom";
import {
  CompareTableSkeleton,
  ResultTabNewSkeleton,
  ResultTabSkeleton,
} from "../Skeleton";
import TOCStickyLayout from "./tocStickyLayout";
import { collapseAtom } from "../../utils/atoms/collapseAtom";
import { buttons } from "./RESULT_CONFIG";
import "./result.scss";
import { defaultKpiFactors, isHtmlTag, mgaRegex, useAssessmentRouteParams } from "../../constants/helper";
import SEConfirmModal from "../common/SEConfirmModal";
import { useNavigate } from "react-router-dom";
import { activeTabAtom } from "../../utils/atoms/activeTabAtom";
import { tourAtom } from "../../utils/atoms/tourAtom";
import { useTranslation } from "react-i18next";
import displayError from "../../constants/utils";

const MGA_API = new MGAAPI();

const DESC_LIMIT = 50;
const truncateAtWord = (text, limit) => {
  if (!text || text.length <= limit) return text;
  const cut = text.lastIndexOf(" ", limit);
  return `${text.slice(0, cut > 0 ? cut : limit)}...`;
};
const isJsonString = (str) => {
  if (!str || typeof str !== 'string') return false;
  const trimmed = str.trim();
  if (trimmed[0] !== '{' && trimmed[0] !== '[') return false;
  try { JSON.parse(trimmed); return true; } catch (_) { return false; }
};
const SPECIAL_VALUES = new Set(['completeMicrogrid', 'referenceExistingMicrogrid']);
const SelectItem = ({ label, description, selected, value, expandedItem, setExpandedItem, t, ...others }) => {
  const isLong = description && description.length > DESC_LIMIT;
  const expanded = expandedItem === value;
  const displayText = expanded || !isLong ? description : truncateAtWord(description, DESC_LIMIT);
  return (
    <div {...others} style={{ ...others.style, overflow: "hidden", maxWidth: "100%" }}>
      <div>{label}</div>
      {description && !isJsonString(description) && !SPECIAL_VALUES.has(value) && (
        <div style={{ display: "flex" }}>
          <div style={{ fontSize: 12, color: selected ? "#fff" : "#979797", wordBreak: "break-all", width: "80%" }}>
            {displayText}
          </div>
          {isLong && (
            <button
              type="button"
              aria-expanded={expanded}
              style={{ fontSize: 11, color: selected ? "#fff" : "#979797", cursor: "pointer", userSelect: "none", width: "20%", textAlign: "right", textDecoration: "underline", textUnderlineOffset: "2px", border: 0, background: "transparent", padding: 0, fontFamily: "inherit" }}
              onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
              onClick={(e) => { e.stopPropagation(); e.preventDefault(); setExpandedItem(expanded ? null : value); }}
            >
              {expanded ? t("result.index.selectSimulation.showLess") : t("result.index.selectSimulation.showMore")}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const Result = () => {
  const { t } = useTranslation();
  // Error modal state and handler
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useAtom(activeTabAtom);

  const [collapseSideMenu] = useAtom(collapseAtom);
  const [projectDetails] = useAtom(projectDetailsAtom);
  const [dropdownOptions, setDropdownOptions] = useState([]);
  const [selectedValue, setSelectedValue] = useState(null);
  const [resultTimeSeries, setResultTimeSeries] = useState([]);
  const workerRef = useRef();
  const [isLoading, setIsLoading] = useState(true);
  const [resultSimulationData, setResultSimulationData] = useState(null);
  const [showScenario, setShowScenario] = useState({});
  const [showCompleteMicrogrid, setShowCompleteMicrogrid] = useState(false);
  const [simulationData, setSimulationData] = useState({});
  const [gridType, setGridType] = useState(null);
  const { projectId, designId, designVersionId } = useAssessmentRouteParams();
  const resultTabView = "96%"
  const [selectedMicrogridId, setSelectedMicrogridId] = useState(null);
  const [kpiFactorsData, setKpiFactorsData] = useState([]);
  const [kpiProjectSettings, setKpiProjectSettings] = useState({});
  const [isCompareTransitionLoading, setIsCompareTransitionLoading] = useState(false);
  const [compareTransitionType, setCompareTransitionType] = useState(null);
  const [expandedItem, setExpandedItem] = useState(null);
  const compareTransitionTimerRef = useRef(null);
  const [tour] = useAtom(tourAtom);
  const userScope = JSON.parse(localStorage.getItem('scope'));
  const [simulationNameLookupMap, setSimulationNameLookupMap] = useState([]);
  // const projectDesignVersion =
  //   projectDetails?.designWithVersionsDTOList?.[0]?.designVersionDTO?.[0];

  const handleClickCompare = () => {
    const isOpeningCompare = !showCompleteMicrogrid;
    setCompareTransitionType(isOpeningCompare ? "open" : "close");
    setIsCompareTransitionLoading(true);

    if (compareTransitionTimerRef.current) {
      clearTimeout(compareTransitionTimerRef.current);
    }

    compareTransitionTimerRef.current = setTimeout(() => {
      setShowCompleteMicrogrid((prev) => !prev);
      compareTransitionTimerRef.current = setTimeout(() => {
        setIsCompareTransitionLoading(false);
        setCompareTransitionType(null);
      }, 250);
    }, 30);
  };

  const handleSelectChange = (value) => {
    const selectedOption = dropdownOptions.find((option) => option.value === value);
    setSelectedValue(value);
    setSelectedMicrogridId(selectedOption?.id);
  };

  const handleDropdownData = () => {
    const selectedData = simulationData?.[selectedValue];

    setResultSimulationData(selectedData);
  };
  const handleErrorModalOk = () => {
    setErrorModalOpen(false);
    setActiveTab('DESIGN');
    navigate(`/assessment/${projectId}/${designId}/${designVersionId}?tab=design`);
  };
  const fetchDropdownOptions = () => {
    if (designId && designVersionId && projectId) {
      setIsLoading(true);
      MGA_API.simulationData(projectId, designId, designVersionId)
        .then((res) => {
          if (res && Object.keys(res).length === 0 && res.constructor === Object) {
            setIsLoading(false);
            // setErrorModalOpen(true);
            // return;
          }else if (res) {
            // Order by simulationName; keys missing from it are appended.
            const scenarioData = {};
            (res?.simulationName || []).forEach(({ value }) => {
              if (res.simulationData?.[value]) scenarioData[value] = res.simulationData[value].scenario;
            });
            Object.keys(res?.simulationData || {}).forEach((key) => {
              if (!(key in scenarioData)) scenarioData[key] = res.simulationData[key]?.scenario;
            });
          
          setShowScenario(scenarioData);
          const simulationOptions = res?.simulationName || [];
          const simulationDataKeys = res?.simulationData ? Object.keys(res.simulationData) : [];
          const simulatioNameValueLabel = Object.fromEntries(simulationOptions.map(item => [item.value, item.label]));
          let filteredOptions = simulationOptions.filter((opt) =>
            simulationDataKeys.includes(String(opt?.value))
        );
            const specialValues = [
              "completeMicrogrid",
              "referenceExistingMicrogrid",
            ];
            const specialOptions = filteredOptions.filter((option) =>
              specialValues.includes(option?.value)
            );
            const reorderedOptions = filteredOptions.filter(
              (option) => !specialValues.includes(option?.value)
            );
            if (specialOptions.length) reorderedOptions.push(...specialOptions);
            const finalOptions = reorderedOptions.length ? reorderedOptions : filteredOptions;
            setDropdownOptions(finalOptions);
            setSelectedValue(finalOptions?.[0]?.value);
            setSelectedMicrogridId(finalOptions?.[0]?.id);
            setSimulationData(res?.simulationData);
            setGridType(res?.gridType);
            setSimulationNameLookupMap(simulatioNameValueLabel);
            setIsLoading(false);
          }
        })
        .catch((error) => {
          setIsLoading(false);
          console.log("Error fetching simulation Data:", error);
        });
    }

  };

  const fetchTimeSeriesData = () => {
    if (designId && designVersionId && projectId) {
      MGA_API.getTimeSeriesData(
        projectId,
        designId,
        designVersionId,
        selectedValue
      )
        .then((res) => {
          if (res) {
            // Use Web Worker to downsample large time series data
            setIsLoading(false);
            // if (!workerRef.current) {
            //   workerRef.current = new window.Worker(
            //     new URL("../../workers/timeSeriesWorker.js", import.meta.url)
            //   );
            // }
            // workerRef.current.onmessage = (e) => {
              setResultTimeSeries(res);
            // };
            // // You can adjust maxPoints as needed for performance/quality
            // workerRef.current.postMessage({ data: res, maxPoints: 2000 });
          }
        })
        .catch((error) => {
          setIsLoading(false);
          console.log("Error fetching TimeSeries:", error);
        });
    }
  };
  // Clean up worker on unmount
  useEffect(() => {
    return () => {
      if (compareTransitionTimerRef.current) {
        clearTimeout(compareTransitionTimerRef.current);
        compareTransitionTimerRef.current = null;
      }
      if (workerRef.current) {
        workerRef.current.terminate();
        workerRef.current = null;
      }
    };
  }, []);

    const fetchKpiFactors = (projectId, designId, designVersionId) => {
      MGA_API.getkpiFactors(projectId, designId, designVersionId)
        .then((res) => {
          setKpiFactorsData(res?.kpiFactors?.length ? res?.kpiFactors : defaultKpiFactors);
          setKpiProjectSettings(res?.projectSetting || {});
        })
        .catch((error) => {
          console.log("Error fetching KPI Factors:", error);
          setKpiFactorsData([]);
        });
    };

  useEffect(() => {
    fetchDropdownOptions();
    fetchKpiFactors(projectId, designId, designVersionId);
  }, [designVersionId]);

  useEffect(() => {
    if (selectedValue) {
      setResultTimeSeries({});
      handleDropdownData();
      fetchTimeSeriesData();
    }
  }, [selectedValue]);

  // Ref for TOCStickyLayout
  const tocLayoutRef = useRef(null);

  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [exportFormat, setExportFormat] = useState("pdf");
  const [showExportTariffModal, setShowExportTariffModal] = useState(false);
  const [exportTariffEmail, setExportTariffEmail] = useState("");
  const [exportTariffProjectTitle, setExportTariffProjectTitle] = useState("");
  const [exportTariffLoading, setExportTariffLoading] = useState(false);

  const exportTariffProjectTitleError = useMemo(() => {
    const val = exportTariffProjectTitle || '';
    const trimmed = val.trim();
    if (trimmed.length === 0) return t("result.index.exportConfiguration.projectTitleValidation");
    if (trimmed.length < 5) return t("result.index.exportConfiguration.projectTitleMinLength");
    if (!mgaRegex.leadingTrailingInternalSpacesRegex.test(val)) return t("result.index.exportConfiguration.projectTitleSpaces");
    if (isHtmlTag(val)) return t("result.index.exportConfiguration.projectTitleHtmlTags");
    return null;
  }, [exportTariffProjectTitle, t]);

  const exportTariffEmailValid = useMemo(() => {
    const s = (exportTariffEmail || '').trim();
    if (!s || s.length > 254) return false;
    if (s.includes(' ')) return false;
    const at = s.indexOf('@');
    if (at <= 0 || at !== s.lastIndexOf('@')) return false;
    const local = s.slice(0, at);
    const domain = s.slice(at + 1);
    if (!local || !domain) return false;
    const dot = domain.indexOf('.');
    if (dot <= 0 || dot === domain.length - 1) return false;
    if (domain.split('.').some(lbl => lbl.length === 0)) return false;
    return true;
  }, [exportTariffEmail]);

  const handleExportReport = async (format) => {
    if (
      tocLayoutRef.current &&
      typeof tocLayoutRef.current.handleDownloadSections === "function"
    ) {
      await tocLayoutRef.current.handleDownloadSections(format);
    }
  };

  const openExportConfirm = (format) => {
    setExportFormat(format);
    setShowDownloadModal(true);
  };

  const handleExportTariff = () => {
    setExportTariffLoading(true);
    MGA_API.exportTariffConfiguration(projectId, designId, designVersionId, exportTariffEmail, exportTariffProjectTitle)
      .then((res) => {
        setExportTariffLoading(false);
        setShowExportTariffModal(false);
        setExportTariffEmail("");
        displayError("success", res?.message || t("result.index.exportConfiguration.success"));
      })
      .catch((error) => {
        setExportTariffLoading(false);
        setShowExportTariffModal(false);
        setExportTariffEmail("");
        displayError("error", error?.response?.data?.message || t("result.index.exportConfiguration.error"));
      });
  };

  return (
    <>
      <SEConfirmModal
        opened={errorModalOpen}
        onClose={handleErrorModalOk}
        onConfirm={handleErrorModalOk}
        header={t("result.index.error.header")}
        message={
          <>
            {t("result.index.error.messageLine1")}<br />
            {t("result.index.error.messageLine2")}
          </>
        }
        confirmLabel={t("common.ok")}
        onlyOk={true}
      />
      <div
        style={{
          position: "sticky",
          top: "0px",
          zIndex: "10",
          cursor: "pointer",
        }}
      >
        <ManageProgressBar />
      </div>
      <div className="w-100">
        {isLoading ? (
          <ResultTabSkeleton />
        ) : (
          <>
            {!showCompleteMicrogrid && (
              <>
                <Grid gutter="md" className="mt-2 mb-2 justify-content-center">
                  <Grid.Col span={4}>
                    <Select
                      label={t("result.index.selectSimulation.label")}
                      placeholder={t("result.index.selectSimulation.placeholder")}
                      data={
                        dropdownOptions?.length > 0
                          ? dropdownOptions.map((opt) => ({ ...opt, expandedItem, setExpandedItem, t }))
                          : [{ value: "No Simulation Type", label: t("result.index.selectSimulation.none"), disabled: true }]
                      }
                      value={selectedValue || dropdownOptions?.[0]?.value}
                      onChange={handleSelectChange}
                      itemComponent={SelectItem}
                      withinPortal
                      searchable
                      onDropdownClose={() => setExpandedItem(null)}
                    />
                  </Grid.Col>
                  <Grid.Col
                    span={4}
                    style={{
                      display: "flex",
                      alignItems: "flex-end",
                      columnGap: "0.5rem",
                    }}
                  >
                    {buttons?.map((button) => {
                      if (button === "Export Configuration") {
                        return (
                          <Button
                            key={button}
                            variant="default"
                            style={{ fontWeight: 400 }}
                            onClick={() => {
                            setExportTariffProjectTitle(projectDetails?.projectDTO?.projectName || "");
                            setShowExportTariffModal(true);
                          }}
                          disabled={!userScope?.access?.exportTariffConfiguration}
                          >
                            {button}
                          </Button>
                        );
                      }
                      if (button === "Export Report") {
                        return (

                          <Menu key={button} withinPortal={false} position="bottom-end">
                            <Menu.Target>
                              <Button
                                variant="default"
                                style={{ fontWeight: 400 }}
                              >
                               {t("result.index.button.exportReport")}
                              </Button>
                            </Menu.Target>
                            <Menu.Dropdown>
                              <Menu.Item onClick={() => openExportConfirm("pdf")}>
                                PDF
                              </Menu.Item>
                              <Menu.Item onClick={() => openExportConfirm("docx")}>
                                DOCX
                              </Menu.Item>
                            </Menu.Dropdown>
                          </Menu>
                        );
                      }
                      return (
                        <Button
                          onClick={handleClickCompare}
                          key={button}
                          variant="default"
                          style={{ fontWeight: 400 }}
                          disabled={button !== "Compare All"}
                        >
                          {button}
                        </Button>
                      );
                    })}
                  </Grid.Col>
                </Grid>
                {(() => {
                  const desc = dropdownOptions.find((opt) => opt.value === selectedValue)?.description;
                  return desc && !isJsonString(desc) && !SPECIAL_VALUES.has(selectedValue) ? (
                    <Grid gutter="md" className="mt-2 mb-2 justify-content-center">
                      <Grid.Col span={8} style={{ fontSize: 13, color: "#979797", wordBreak: "break-word" }}>
                        {desc}
                      </Grid.Col>
                    </Grid>
                  ) : null;
                })()}
              </>
            )}
            {selectedValue && resultSimulationData?.energyBalance ? (
              <Grid
                gutter="md"
                className="mt-2 mb-2"
                style={{
                  position: showCompleteMicrogrid ? "" : (tour ? "" : "fixed"),
                  width: showCompleteMicrogrid ? "100%" : resultTabView,
                  padding: "2%",
                  paddingTop: 0,
                }}
              >
                {isCompareTransitionLoading ? (
                  <Grid.Col span={12} style={{ width: "100%" }}>
                    {compareTransitionType === "open" ? (
                      <CompareTableSkeleton />
                    ) : (
                      <ResultTabNewSkeleton />
                    )}
                  </Grid.Col>
                ) : (
                  <TOCStickyLayout
                    ref={tocLayoutRef}
                    resultTimeSeries={resultTimeSeries}
                    resultSimulationData={resultSimulationData}
                    showCompleteMicrogrid={showCompleteMicrogrid}
                    showScenario={showScenario}
                    handleClickCompare={handleClickCompare}
                    simulationData={simulationData}
                    simulationNameLookupMap={simulationNameLookupMap}
                    selectedValue={selectedValue}
                    selectedMicrogridId={selectedMicrogridId}
                    kpiFactorsData={kpiFactorsData}
                    kpiProjectSettings={kpiProjectSettings}
                    gridType={gridType}
                  />
                )}
                {/* Download confirmation modal */}
                <SEConfirmModal
                  opened={showDownloadModal}
                  onClose={() => setShowDownloadModal(false)}
                  onConfirm={async () => {
                    setShowDownloadModal(false);
                    await handleExportReport(exportFormat);
                  }}
                  header={t("result.index.download.header")}
           //       message={t("result.index.download.message")}
                  confirmLabel={t("common.yes")}
                  cancelLabel={t("common.no")}
                  message={`Are you sure you want to download the report in ${exportFormat?.toUpperCase()} format?`}

                />
                <SEConfirmModal
                  opened={showExportTariffModal}
                  onClose={() => {
                    if (exportTariffLoading) return;
                    setShowExportTariffModal(false);
                    setExportTariffEmail("");
                  }}
                  onConfirm={handleExportTariff}
                  header={t("result.index.exportConfiguration.header")}
                  message={
                    <>
                     <div style={{ marginBottom: "1rem" }}>
                        {t("result.index.exportConfiguration.message")}
                      </div>
                      <TextInput
                        label={t("result.index.exportConfiguration.projectTitleLabel")}
                        value={exportTariffProjectTitle}
                        onChange={(e) => { setExportTariffProjectTitle(e.target.value); }}
                        error={exportTariffProjectTitleError}
                        placeholder={t("result.index.exportConfiguration.projectTitlePlaceholder")}
                        maxLength={100}
                        mb="sm"
                        required
                      />
                      <TextInput
                        label={t("result.index.exportConfiguration.emailLabel")}
                        value={exportTariffEmail}
                        onChange={(e) => setExportTariffEmail(e.target.value)}
                        error={exportTariffEmail && !exportTariffEmailValid ? t("result.index.exportConfiguration.emailValidation") : null}
                        placeholder="email@example.com"
                        required
                      />
                    </>
                  }
                  confirmLabel={t("common.ok")}
                  cancelLabel={t("common.cancel")}
                  confirmDisabled={!!exportTariffProjectTitleError || !exportTariffEmailValid}
                  confirmLoading={exportTariffLoading}
                  confirmLoadingLabel={t("result.index.exportConfiguration.exporting")}
                />
              </Grid>
            ) : (
              <ResultTabNewSkeleton />
            )}
          </>
        )}
      </div>
    </>
  );
};

export default Result;
